import {UploadStrategy, UploadStrategyConfig} from './upload-strategy';
import {UploadedFile} from '@ui/utils/files/uploaded-file';
import axios, {AxiosInstance, AxiosProgressEvent} from 'axios';
import {FileEntry} from '../../file-entry';
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setInLocalStorage,
} from '@ui/utils/hooks/local-storage';
import {apiClient} from '@common/http/query-client';
import {getAxiosErrorMessage} from '@common/http/get-axios-error-message';
import axiosRetry from 'axios-retry';

const oneMB = 1024 * 1024;
// chunk size that will be uploaded to s3 per request
const desiredChunkSize = 20 * oneMB;
// how many urls should be pre-signed per call to backend
const batchSize = 10;
// number of concurrent requests to s3 api
const concurrency = 4;
const maxPartAttempts = 6;
const etagSyncAttempts = 4;

interface ChunkState {
  blob: Blob | File;
  done: boolean;
  etag?: string;
  partNumber: number;
  bytesUploaded: number;
}

interface SignedUrl {
  url: string;
  partNumber: number;
}

interface StoredUrl {
  createdAt: string;
  uploadId: string;
  fileKey: string;
}

interface UploadedPart {
  PartNumber: number;
  ETag: string;
  Size: string | number;
  LastModified: string;
}

export class S3MultipartUpload implements UploadStrategy {
  private abortController: AbortController;
  private chunks: ChunkState[] = [];
  private uploadId?: string;
  private fileKey?: string;
  private readonly chunkAxios: AxiosInstance;
  private abortedByUser = false;
  private uploadedParts?: UploadedPart[];
  private partAttempts = new Map<number, number>();
  private lastErrorMessage?: string;

  get storageKey(): string {
    return `s3-multipart::${this.file.fingerprint}`;
  }

  constructor(
    private file: UploadedFile,
    private config: UploadStrategyConfig,
  ) {
    this.abortController = new AbortController();
    this.chunkAxios = axios.create();
    axiosRetry(this.chunkAxios, {
      retries: 4,
      shouldResetTimeout: true,
      retryDelay: retryCount => Math.min(1000 * 2 ** retryCount, 10000),
    });
  }

  async start() {
    const storedUrl = getFromLocalStorage<StoredUrl>(this.storageKey);
    if (storedUrl) {
      await this.getUploadedParts(storedUrl);
    }

    if (!this.uploadId || !this.fileKey) {
      await this.createMultipartUpload();
      if (!this.uploadId || !this.fileKey) return;
    }

    this.prepareChunks();

    const result = await this.uploadParts();
    if (result === 'done') {
      const isCompleted = await this.completeMultipartUpload();
      if (!isCompleted) return;

      // catch any errors so below "onError" handler gets executed
      try {
        const response = await this.createFileEntry();
        if (response?.fileEntry) {
          this.config.onSuccess?.(response?.fileEntry, this.file);
          removeFromLocalStorage(this.storageKey);
          return;
        }
      } catch (err) {
        this.lastErrorMessage =
          getAxiosErrorMessage(err) ||
          'O arquivo foi enviado, mas nao foi possivel registrar o video. Tente novamente.';
      }
    }

    // upload failed
    if (!this.abortController.signal.aborted) {
      this.abortController.abort();
    }
    if (!this.abortedByUser) {
      this.config.onError?.(
        this.lastErrorMessage ||
          'Nao foi possivel concluir o upload. Verifique a conexao e tente novamente; arquivos grandes continuam das partes ja enviadas.',
        this.file,
      );
    }
  }

  async abort() {
    this.abortedByUser = true;
    this.abortController.abort();
    await this.abortUploadOnS3();
  }

  abortCurrentRequest() {
    this.abortController.abort();
    return Promise.resolve();
  }

  private async uploadParts(): Promise<any> {
    if (this.abortController.signal.aborted) return;

    const pendingChunks = this.chunks.filter(c => !c.done);
    if (!pendingChunks.length) {
      return Promise.resolve('done');
    }

    const signedUrls = await this.batchSignUrls(
      pendingChunks.slice(0, batchSize),
    );
    if (!signedUrls) return;

    while (signedUrls.length) {
      const batch = signedUrls.splice(0, concurrency);
      const pendingUploads = batch.map(item => {
        return this.uploadPartToS3(item);
      });
      const result = await Promise.all(pendingUploads);
      if (this.abortController.signal.aborted) return;

      const failedParts = batch.filter((item, index) => !result[index]);
      if (failedParts.length) {
        const canRetry = failedParts.every(item =>
          this.markPartAttempt(item.partNumber),
        );
        if (!canRetry) {
          return;
        }

        await this.waitBeforeRetry();
        return this.uploadParts();
      }
    }

    return await this.uploadParts();
  }

  private async batchSignUrls(
    batch: ChunkState[],
  ): Promise<SignedUrl[] | undefined> {
    const response = await this.chunkAxios
      .post(
        'api/v1/s3/multipart/batch-sign-part-urls',
        {
          partNumbers: batch.map(i => i.partNumber),
          uploadId: this.uploadId,
          key: this.fileKey,
        },
        {signal: this.abortController.signal},
      )
      .then(r => r.data as {urls: SignedUrl[]})
      .catch(err => {
        this.lastErrorMessage =
          getAxiosErrorMessage(err) ||
          'Nao foi possivel preparar as partes do upload. Tente novamente em alguns instantes.';
      });

    return response?.urls;
  }

  private async uploadPartToS3({
    url,
    partNumber,
  }: SignedUrl): Promise<boolean | void> {
    const chunk = this.chunks.find(c => c.partNumber === partNumber);
    if (!chunk) return;
    chunk.bytesUploaded = 0;
    this.emitProgress();

    return this.chunkAxios
      .put(url, chunk.blob, {
        withCredentials: false,
        signal: this.abortController.signal,
        onUploadProgress: (e: AxiosProgressEvent) => {
          if (!e.event.lengthComputable) return;

          chunk.bytesUploaded = e.loaded;
          this.emitProgress();
        },
      })
      .then(r => {
        const etag = this.extractEtag(r.headers);
        if (etag) {
          this.markPartDone(chunk, etag);
          return true;
        }

        return this.confirmUploadedPart(partNumber).then(confirmedEtag => {
          if (confirmedEtag) {
            this.markPartDone(chunk, confirmedEtag);
            return true;
          }

          this.lastErrorMessage =
            'Uma parte foi enviada, mas o storage ainda nao confirmou o recebimento. O envio sera retomado automaticamente.';
        });
      })
      .catch(err => {
        chunk.bytesUploaded = 0;
        this.emitProgress();

        if (!this.abortController.signal.aborted && err !== undefined) {
          this.lastErrorMessage = this.getUploadPartErrorMessage(err);
          return false;
        }
      });
  }

  private async createMultipartUpload(): Promise<void> {
    const response = await apiClient
      .post('s3/multipart/create', {
        filename: this.file.name,
        mime: this.file.mime,
        size: this.file.size,
        extension: this.file.extension,
        ...this.config.metadata,
      })
      .then(r => r.data as {uploadId: string; key: string})
      .catch(err => {
        if (err.code !== 'ERR_CANCELED') {
          this.lastErrorMessage = getAxiosErrorMessage(err);
          this.config.onError?.(getAxiosErrorMessage(err), this.file);
        }
      });

    if (response) {
      this.uploadId = response.uploadId;
      this.fileKey = response.key;
      setInLocalStorage(this.storageKey, {
        createdAt: new Date().toISOString(),
        fileKey: this.fileKey,
        uploadId: this.uploadId,
      } as StoredUrl);
    }
  }

  private async getUploadedParts({fileKey, uploadId}: StoredUrl) {
    const parts = await this.fetchUploadedParts(fileKey, uploadId, true);
    if (parts) {
      this.uploadId = uploadId;
      this.fileKey = fileKey;
    }
  }

  private async completeMultipartUpload(): Promise<{location: string} | null> {
    return apiClient
      .post('s3/multipart/complete', {
        key: this.fileKey,
        uploadId: this.uploadId,
        parts: this.chunks.map(c => {
          return {
            ETag: c.etag,
            PartNumber: c.partNumber,
          };
        }),
      })
      .then(r => r.data)
      .catch(err => {
        this.lastErrorMessage =
          getAxiosErrorMessage(err) ||
          'O arquivo foi enviado, mas nao foi possivel finalizar o upload multipart. Tente novamente.';
        this.config.onError?.(this.lastErrorMessage, this.file);
      });
  }

  private async createFileEntry(): Promise<{fileEntry: FileEntry}> {
    return await apiClient
      .post('s3/entries', {
        ...this.config.metadata,
        clientMime: this.file.mime,
        clientName: this.file.name,
        filename: this.fileKey!.split('/').pop(),
        size: this.file.size,
        clientExtension: this.file.extension,
      })
      .then(r => r.data);
  }

  private prepareChunks() {
    this.chunks = [];
    // at least 5MB per request, at most 10k requests
    const minChunkSize = Math.max(5 * oneMB, Math.ceil(this.file.size / 10000));
    const chunkSize = Math.max(desiredChunkSize, minChunkSize);

    // Upload zero-sized files in one zero-sized chunk
    if (this.file.size === 0) {
      this.chunks.push({
        blob: this.file.native,
        done: false,
        partNumber: 1,
        bytesUploaded: 0,
      });
    } else {
      let partNumber = 1;
      for (let i = 0; i < this.file.size; i += chunkSize) {
        const end = Math.min(this.file.size, i + chunkSize);
        // check if this part was already uploaded previously
        const previouslyUploaded = this.uploadedParts?.find(
          p => p.PartNumber === partNumber,
        );
        this.chunks.push({
          blob: this.file.native.slice(i, end),
          done: !!previouslyUploaded,
          partNumber,
          etag: previouslyUploaded ? previouslyUploaded.ETag : undefined,
          bytesUploaded: previouslyUploaded?.Size
            ? Number(previouslyUploaded.Size)
            : 0,
        });
        partNumber++;
      }
    }
  }

  private abortUploadOnS3() {
    return apiClient.post('s3/multipart/abort', {
      key: this.fileKey,
      uploadId: this.uploadId,
    });
  }

  private emitProgress() {
    const totalUploaded = this.chunks.reduce(
      (n, c) => n + c.bytesUploaded,
      0,
    );

    this.config.onProgress?.({
      bytesUploaded: Math.min(totalUploaded, this.file.size),
      bytesTotal: this.file.size,
    });
  }

  private markPartAttempt(partNumber: number): boolean {
    const attempts = (this.partAttempts.get(partNumber) || 0) + 1;
    this.partAttempts.set(partNumber, attempts);
    return attempts <= maxPartAttempts;
  }

  private waitBeforeRetry(): Promise<void> {
    const highestAttempt = Math.max(1, ...this.partAttempts.values());
    const delay = Math.min(1000 * 2 ** highestAttempt, 30000);
    return this.wait(delay);
  }

  private async confirmUploadedPart(
    partNumber: number,
  ): Promise<string | undefined> {
    if (!this.fileKey || !this.uploadId || this.abortController.signal.aborted) {
      return;
    }

    for (let attempt = 1; attempt <= etagSyncAttempts; attempt++) {
      const parts = await this.fetchUploadedParts(this.fileKey, this.uploadId);
      const uploadedPart = parts?.find(part => part.PartNumber === partNumber);

      if (uploadedPart?.ETag) {
        return uploadedPart.ETag;
      }

      if (attempt < etagSyncAttempts) {
        await this.wait(750 * attempt);
      }
    }
  }

  private async fetchUploadedParts(
    fileKey: string,
    uploadId: string,
    removeStoredUploadOnClientError = false,
  ): Promise<UploadedPart[] | null> {
    const response = await apiClient
      .post('s3/multipart/get-uploaded-parts', {
        key: fileKey,
        uploadId,
      })
      .then(r => r.data as {parts: UploadedPart[]})
      .catch(err => {
        const status = axios.isAxiosError(err) ? err.response?.status : null;
        if (removeStoredUploadOnClientError && status && status >= 400 && status < 500) {
          removeFromLocalStorage(this.storageKey);
        } else {
          this.lastErrorMessage =
            getAxiosErrorMessage(err) ||
            'Nao foi possivel consultar as partes ja enviadas. Tente novamente.';
        }
        return null;
      });

    if (!response?.parts) {
      return null;
    }

    this.uploadedParts = response.parts;
    return response.parts;
  }

  private markPartDone(chunk: ChunkState, etag: string): void {
    chunk.done = true;
    chunk.etag = etag;
    chunk.bytesUploaded = chunk.blob.size;
    this.emitProgress();
  }

  private extractEtag(headers: unknown): string | undefined {
    const headerMap = headers as {
      get?: (name: string) => unknown;
      etag?: unknown;
      ETag?: unknown;
    };
    const value = headerMap.get?.('etag') ?? headerMap.etag ?? headerMap.ETag;
    const etag = Array.isArray(value) ? value[0] : value;

    if (typeof etag === 'string' && etag.trim()) {
      return etag;
    }
  }

  private wait(delay: number): Promise<void> {
    return new Promise(resolve => window.setTimeout(resolve, delay));
  }

  private getUploadPartErrorMessage(err: unknown): string {
    if (axios.isAxiosError(err)) {
      if (err.code === 'ERR_NETWORK') {
        return 'Conexao interrompida durante o envio. O upload sera retomado automaticamente.';
      }

      if (err.response?.status) {
        return `O storage recusou uma parte do upload (${err.response.status}). O upload sera retomado automaticamente.`;
      }

      if (err.message) {
        return err.message;
      }
    }

    return 'Uma parte do upload falhou. O upload sera retomado automaticamente.';
  }

  static async create(
    file: UploadedFile,
    config: UploadStrategyConfig,
  ): Promise<S3MultipartUpload> {
    return new S3MultipartUpload(file, config);
  }
}
